'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { AlertTriangle, ShieldAlert, CheckCircle2, Info, X, ArrowRight } from 'lucide-react';

export interface CmsNotificationData {
  announcement: string | null;
  notificationType?: 'INFO' | 'WARNING' | 'CRITICAL' | 'SUCCESS';
  notificationLink?: string;
  notificationLinkText?: string;
  isDismissible?: boolean;
  targetAudience?: 'ALL' | 'PUBLIC_ONLY' | 'AUTHENTICATED_ONLY';
  maintenanceMode?: boolean;
}

interface NotificationDismissWrapperProps {
  data: CmsNotificationData;
  noticeSignature: string;
}

export function NotificationDismissWrapper({
  data,
  noticeSignature,
}: NotificationDismissWrapperProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (data.isDismissible && noticeSignature) {
      const dismissedInStorage = localStorage.getItem('postbrix_dismissed_notice') === noticeSignature;
      if (dismissedInStorage) {
        setIsDismissed(true);
      }
    }
  }, [data.isDismissible, noticeSignature]);

  const handleDismiss = () => {
    if (noticeSignature) {
      localStorage.setItem('postbrix_dismissed_notice', noticeSignature);
    }
    setIsDismissed(true);
  };

  if (isDismissed) return null;

  const type = data.notificationType || 'INFO';

  const typeStyles = {
    INFO: {
      bg: 'bg-pb-slate-950/95 border-b border-pb-blue-500/20 text-pb-slate-100 shadow-pb-blue-950/20',
      badge: 'bg-pb-blue-500/10 border-pb-blue-500/30 text-pb-blue-300',
      dot: 'bg-pb-blue-400',
      dotPing: 'bg-pb-blue-400',
      icon: Info,
    },
    WARNING: {
      bg: 'bg-pb-slate-950/95 border-b border-amber-500/20 text-pb-slate-100 shadow-amber-950/20',
      badge: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
      dot: 'bg-amber-400',
      dotPing: 'bg-amber-400',
      icon: AlertTriangle,
    },
    CRITICAL: {
      bg: 'bg-pb-slate-950/95 border-b border-rose-500/20 text-pb-slate-100 shadow-rose-950/20',
      badge: 'bg-rose-500/10 border-rose-500/30 text-rose-300',
      dot: 'bg-rose-400',
      dotPing: 'bg-rose-400',
      icon: ShieldAlert,
    },
    SUCCESS: {
      bg: 'bg-pb-slate-950/95 border-b border-emerald-500/20 text-pb-slate-100 shadow-emerald-950/20',
      badge: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
      dot: 'bg-emerald-400',
      dotPing: 'bg-emerald-400',
      icon: CheckCircle2,
    },
  };

  const currentStyle = typeStyles[type];
  const IconComponent = currentStyle.icon;

  return (
    <div className={`relative z-50 w-full transition-all duration-300 backdrop-blur-xl ${currentStyle.bg} shadow-md`}>
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-2 text-xs font-medium sm:px-6">
        <div className="flex flex-1 items-center justify-center gap-2.5 text-center sm:justify-start">
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${currentStyle.badge}`}>
            <span className="relative flex h-1.5 w-1.5">
              <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 ${currentStyle.dotPing}`} />
              <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${currentStyle.dot}`} />
            </span>
            <IconComponent size={11} className="shrink-0" />
            {data?.maintenanceMode ? 'Maintenance' : type}
          </span>

          <span className="truncate text-pb-slate-200 text-xs sm:text-[13px] font-medium tracking-normal">
            {data?.announcement}
          </span>
        </div>

        {Boolean(data?.notificationLink && data.notificationLink.trim() !== '') && (
          <div className="flex items-center gap-2 shrink-0">
            <Link
              href={data.notificationLink!}
              prefetch={false}
              className="inline-flex items-center gap-1.5 rounded-full bg-pb-white/10 hover:bg-pb-white/20 border border-pb-white/15 px-3 py-1 text-[11px] font-semibold text-pb-white backdrop-blur-md transition-all hover:scale-[1.02] active:scale-[0.98] shadow-sm"
            >
              {data.notificationLinkText || 'Learn More'}
              <ArrowRight size={12} className="shrink-0" />
            </Link>
          </div>
        )}

        {data?.isDismissible && (
          <button
            onClick={handleDismiss}
            className="rounded-md p-1 text-pb-slate-400 opacity-80 transition-all hover:opacity-100 hover:bg-pb-white/10 hover:text-pb-white shrink-0"
            aria-label="Dismiss notification"
          >
            <X size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
